import React from "react";
import { useTranslation } from "react-i18next";
import { SessionContext } from "../../types/sessionContext";
import { FormField } from "../../components/FormField";
import { getCurrentDemoConfig } from "../../config/demoSites";

interface Step4FormProps {
    currentStep: number;
    sessionContext: SessionContext;
    updateSessionContext: (newCtx: SessionContext) => void;
}

const Step4Form: React.FC<Step4FormProps> = ({ currentStep, sessionContext, updateSessionContext }) => {
    const { t } = useTranslation();
    const fields = sessionContext.layout.workflow.steps[currentStep].fields;
    const demoConfig = getCurrentDemoConfig();
    const stepConfig = demoConfig.steps.find(step => step.id === currentStep);

    // Get all fields from all previous steps for display
    const allFields = {
        ...(sessionContext.layout.workflow.steps[1]?.fields || {}), // Step 1
        ...(sessionContext.layout.workflow.steps[2]?.fields || {}), // Step 2
        ...(sessionContext.layout.workflow.steps[3]?.fields || {}), // Step 3
        ...fields // Current step
    };

    const handleChange = (field: string, value: any) => {
        const updated = { ...sessionContext };
        updated.layout.workflow.steps[currentStep].fields[field] = value;
        updateSessionContext(updated);
    };

    return (
        <div>
            <h1 className="text-2xl font-semibold">{t(stepConfig?.titleKey || "base_demo.workflow.steps.step4form.title")}</h1>
            <p className="mb-4 text-gray-500">{t("base_demo.step4form.description")}</p>

            {/* Final confirmation info */}
            <div className="mb-6 rounded border border-gray-200 bg-gray-50 p-4">
                <h2 className="mb-3 text-lg font-medium">{t("base_demo.step4form.confirmation.title", "Application Complete")}</h2>
                <p>{t("base_demo.step4form.confirmation.message", "Thank you for completing the application process. Your submission has been received.")}</p>

                {allFields.confirmationNumber && (
                    <div className="mt-4 rounded border border-green-100 bg-green-50 p-3 text-center">
                        <h3 className="font-medium text-green-800">{t("base_demo.step4form.confirmation.number", "Confirmation Number")}</h3>
                        <p className="text-2xl font-bold text-green-700">{allFields.confirmationNumber}</p>
                        <p className="mt-2 text-sm text-green-600">{t("base_demo.step4form.confirmation.save", "Please save this number for your records")}</p>
                    </div>
                )}
            </div>

            {/* Dynamically render fields based on configuration */}
            {stepConfig?.fields.map(fieldConfig => (
                <FormField
                    key={fieldConfig.fieldName}
                    field={fieldConfig}
                    value={fields[fieldConfig.fieldName] || ""}
                    onChange={value => handleChange(fieldConfig.fieldName, value)}
                    allFieldValues={fields}
                />
            ))}

            {/* Final notes and next steps */}
            <div className="mt-6 rounded border border-blue-100 bg-blue-50 p-4 text-blue-700">
                <p className="text-sm">
                    {t(
                        "base_demo.step4form.nextSteps",
                        "You will receive a confirmation email with further instructions. If you have any questions, please contact our support team."
                    )}
                </p>
            </div>
        </div>
    );
};

export default Step4Form;
